import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from "react";
import { CMSData, loadCMS, saveCMS, loadCMSFromSupabase } from "./cms";

type CMSContextType = {
  cms: CMSData;
  loading: boolean;
  updateCMS: (updater: (prev: CMSData) => CMSData) => void;
  resetCMS: () => void;
};

const CMSContext = createContext<CMSContextType | undefined>(undefined);

export const CMSProvider = ({ children }: { children: ReactNode }) => {
  const [cms, setCms] = useState<CMSData>(loadCMS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCMSFromSupabase()
      .then((remote) => {
        if (Object.keys(remote).length > 0) {
          setCms((prev) => ({
            ...prev,
            ...(remote.contact ? { contact: remote.contact } : {}),
            ...(remote.images && Object.keys(remote.images).length > 0
              ? { images: { ...prev.images, ...remote.images } }
              : {}),
          }));
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const updateCMS = useCallback((updater: (prev: CMSData) => CMSData) => {
    setCms((prev) => {
      const next = updater(prev);
      saveCMS(next);
      return next;
    });
  }, []);

  const resetCMS = useCallback(() => {
    localStorage.removeItem("vd-cms");
    const fresh = loadCMS();
    setCms(fresh);
  }, []);

  return (
    <CMSContext.Provider value={{ cms, loading, updateCMS, resetCMS }}>
      {children}
    </CMSContext.Provider>
  );
};

export const useCMS = () => {
  const ctx = useContext(CMSContext);
  if (!ctx) throw new Error("useCMS must be used within CMSProvider");
  return ctx;
};
